import React from 'react';
import { 
  Users, FileText, CheckCircle, Link2, DollarSign, 
  Upload, Send, Clock, UserPlus, Search, Building2,
  GraduationCap, Briefcase, TrendingUp, MessageSquare,
  Shield, Copy, ExternalLink, Target, AlertTriangle, Calculator
} from 'lucide-react';

// Reusable slide content components
const FeatureList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
        <span className="text-gray-600">{item}</span>
      </li>
    ))}
  </ul>
);

const StepList = ({ steps }) => (
  <div className="space-y-4">
    {steps.map((step, i) => (
      <div key={i} className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-gray-800 font-semibold flex-shrink-0">
          {i + 1}
        </div>
        <div>
          <p className="font-medium text-gray-800">{step.title}</p>
          {step.description && <p className="text-sm text-gray-500 mt-1">{step.description}</p>}
        </div>
      </div>
    ))}
  </div>
);

const StatCard = ({ icon, label, value, color = 'yellow' }) => (
  <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
    <div className={`p-2 rounded-lg bg-${color}-500/20`}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-semibold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

export const agentTrainingSlides = [
  // Slide 1: Welcome
  {
    title: "Welcome, Partner!",
    subtitle: "Your guide to the H E Direct Agent Dashboard",
    category: "Getting Started",
    icon: <Briefcase className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <p className="text-lg text-gray-600">
          As an H E Direct partner, you can manage students, submit applications, and earn competitive commissions. This training covers:
        </p>
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={<UserPlus className="w-5 h-5 text-blue-500" />} label="Add Students" value="🎓" />
          <StatCard icon={<FileText className="w-5 h-5 text-green-500" />} label="Submit Apps" value="📄" />
          <StatCard icon={<TrendingUp className="w-5 h-5 text-purple-500" />} label="Track Progress" value="📊" />
          <StatCard icon={<DollarSign className="w-5 h-5 text-orange-500" />} label="Earn Commission" value="💰" />
        </div>
      </div>
    ),
    tips: "Complete your profile and get approved to unlock all features!"
  },

  // Slide 2: Dashboard Overview
  {
    title: "Your Dashboard",
    subtitle: "Understanding your commission and stats",
    category: "Navigation",
    icon: <TrendingUp className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-500/10 to-cyan-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Your dashboard shows your earnings and activity at a glance:</p>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Total Commission", icon: "💰", desc: "Lifetime earnings" },
            { label: "Pending Payout", icon: "⏳", desc: "Available to withdraw" },
            { label: "Paid Out", icon: "✅", desc: "Already received" },
            { label: "Total Referrals", icon: "👥", desc: "Students referred" },
          ].map((item, i) => (
            <div key={i} className="bg-gray-100 rounded-xl p-4 text-center">
              <span className="text-2xl">{item.icon}</span>
              <p className="font-medium text-gray-800 mt-2">{item.label}</p>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Your commission stats update automatically after successful enrollments."
  },

  // Slide 3: Referral Link
  {
    title: "Your Referral Link",
    subtitle: "Share and earn on every student",
    category: "Earnings",
    icon: <Link2 className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Your unique referral link is your key to earning commissions:</p>
        <div className="bg-gray-100 rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500 mb-2">Your Referral Link:</p>
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
            <code className="text-teal-600 text-sm flex-1">https://platform.hedirect.com/register?ref=HEDXXXXX</code>
            <button className="p-2 hover:bg-gray-200 rounded-lg">
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
        <FeatureList items={[
          "Share via email, WhatsApp, or social media",
          "Students who register through your link are linked to you",
          "Earn commission when they successfully enroll",
          "Track all referrals in your dashboard"
        ]} />
      </div>
    ),
    tips: "Add your referral link to your email signature for passive referrals!"
  },

  // Slide 4: Adding Students
  {
    title: "Adding Students",
    subtitle: "Register students under your account",
    category: "Student Management",
    icon: <UserPlus className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { title: "Click '+ Add Student' button", description: "Opens the student registration form" },
          { title: "Enter student details", description: "Name, email, phone, education level" },
          { title: "Add preferences", description: "Preferred countries, field of study, budget" },
          { title: "Save the student", description: "Student is added to your 'My Students' tab" }
        ]} />
        <div className="mt-4 p-3 bg-blue-500/10 rounded-xl border border-blue-500/30">
          <p className="text-sm text-blue-600">💡 Students added this way don&apos;t need their own login - you manage everything!</p>
        </div>
      </div>
    ),
    tips: "Collect complete information upfront to speed up the application process."
  },

  // Slide 5: Uploading Documents
  {
    title: "Uploading Documents",
    subtitle: "Essential documents for applications",
    category: "Student Management",
    icon: <Upload className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Required documents vary by education level:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4">
            <p className="font-medium text-gray-800 mb-3">📚 Undergraduate</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Passport</li>
              <li>• High school transcripts</li>
              <li>• English test score</li>
              <li>• Personal statement</li>
            </ul>
          </div>
          <div className="bg-gray-100 rounded-xl p-4">
            <p className="font-medium text-gray-800 mb-3">🎓 Postgraduate</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Passport</li>
              <li>• Bachelor&apos;s degree & transcripts</li>
              <li>• English test score</li>
              <li>• CV/Resume</li>
              <li>• References</li>
            </ul>
          </div>
        </div>
      </div>
    ),
    tips: "Upload all documents BEFORE submitting to H E Direct for faster processing."
  },

  // Slide 6: Applying for Courses
  {
    title: "Applying for Courses",
    subtitle: "Submit applications on behalf of students",
    category: "Applications",
    icon: <Send className="w-8 h-8 text-cyan-500" />,
    iconBg: "bg-cyan-100",
    bgColor: "bg-gradient-to-r from-cyan-500/10 to-blue-500/10",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { title: "Select a student", description: "From your 'My Students' tab" },
          { title: "Click 'Apply for Course'", description: "Opens the course search" },
          { title: "Search and select a course", description: "Filter by country, university, or field" },
          { title: "Confirm application", description: "Application is created in 'draft' status" }
        ]} />
      </div>
    ),
    tips: "Students can have multiple applications to different universities."
  },

  // Slide 7: Submitting to H E Direct
  {
    title: "Submit to H E Direct",
    subtitle: "The consent and submission process",
    category: "Applications",
    icon: <Shield className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Before H E Direct processes an application, you must give consent:</p>
        <StepList steps={[
          { title: "Verify all documents are uploaded", description: "Check completeness" },
          { title: "Click 'Submit to H E Direct'", description: "On the application card" },
          { title: "Review the consent form", description: "Confirm accuracy of information" },
          { title: "Give consent & submit", description: "Application enters the pipeline" }
        ]} />
        <div className="mt-4 p-3 bg-teal-50 rounded-xl border border-teal-200">
          <p className="text-sm text-teal-600">⚠️ You cannot add documents after submission - ensure everything is complete!</p>
        </div>
      </div>
    ),
    tips: "Double-check all documents before giving consent to avoid delays."
  },

  // Slide 8: Journey Tracking
  {
    title: "Tracking Application Journey",
    subtitle: "Follow your students through 10 stages",
    category: "Progress Tracking",
    icon: <TrendingUp className="w-8 h-8 text-indigo-500" />,
    iconBg: "bg-indigo-500/20",
    bgColor: "bg-gradient-to-r from-indigo-500/10 to-violet-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">As an agent, you see 10 stages in the journey:</p>
        <div className="grid grid-cols-5 gap-2 text-xs">
          {[
            "1. App Received", "2. App Submitted", "3. Offer Issued", "4. Offer Accepted", "5. UCO",
            "6. Deposit", "7. CAS Stage", "8. Visa Issued", "9. Enrolled", "10. Commission"
          ].map((stage, i) => (
            <div key={i} className={`p-2 rounded-lg text-center ${i < 3 ? 'bg-blue-100 text-blue-600' : i < 6 ? 'bg-teal-100 text-teal-600' : 'bg-green-100 text-green-600'}`}>
              {stage}
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-4">
          ✅ Green = Completed | 🟡 Yellow = Current | ⚪ Grey = Upcoming
        </p>
      </div>
    ),
    tips: "You'll receive notifications when stages change - keep your email updated!"
  },

  // Slide 9: Commission Calculator
  {
    title: "Commission Calculator",
    subtitle: "Calculate your potential earnings",
    category: "New Feature",
    icon: <DollarSign className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-emerald-50",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Use the Commission Calculator tab to estimate earnings:</p>
        <StepList steps={[
          { title: "Go to Commission Calculator tab", description: "On your Agent Dashboard" },
          { title: "Select a university", description: "Only universities with commission set appear" },
          { title: "Enter gross tuition fee", description: "Total fee before deductions" },
          { title: "Enter scholarship amount", description: "If student has scholarship" },
          { title: "Click Calculate", description: "See your estimated commission" }
        ]} />
        <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl mt-4">
          <p className="font-bold text-red-800">⚠️ IMPORTANT</p>
          <p className="text-sm text-red-700 mt-1">Commission is payable on <strong>NET FEE ONLY</strong> (after deducting scholarships).</p>
          <p className="text-sm text-red-700 mt-1"><strong>Taxes and fees apply</strong> at the time of raising an invoice.</p>
        </div>
      </div>
    ),
    tips: "The 70/30 split means you receive 70% of the university's commission payment."
  },

  // Slide 10: Bulk Student Upload
  {
    title: "Bulk Student Upload",
    subtitle: "Add multiple students at once via CSV",
    category: "New Feature",
    icon: <Upload className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Save time by uploading multiple students from a spreadsheet:</p>
        <StepList steps={[
          { title: "Click Bulk Upload button", description: "Next to Add Student" },
          { title: "Download the CSV template", description: "Opens in Excel or Google Sheets" },
          { title: "Fill in student details", description: "full_name and email are required" },
          { title: "Upload the CSV file", description: "Preview students before confirming" },
          { title: "Review results", description: "See how many were added successfully" }
        ]} />
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
          <p className="text-sm text-blue-700">💡 For multiple countries, use quotes: &quot;UK, Canada, Germany&quot;</p>
        </div>
      </div>
    ),
    tips: "Great for onboarding multiple students from recruitment events!"
  },

  // Slide 11: Requesting Payouts
  {
    title: "Requesting Commission Payouts",
    subtitle: "Withdraw your earnings",
    category: "Finances",
    icon: <DollarSign className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { title: "Go to Finances page", description: "Via the navigation menu" },
          { title: "Click 'Request Payout'", description: "Minimum £50 required" },
          { title: "Enter amount & bank details", description: "Bank name, account number, sort code" },
          { title: "Submit request", description: "Admin will process within 2-3 business days" }
        ]} />
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-100 rounded-xl p-3 text-center">
            <p className="text-2xl font-semibold text-teal-600">£50</p>
            <p className="text-xs text-gray-500">Minimum payout</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-3 text-center">
            <p className="text-2xl font-semibold text-green-500">2-3 days</p>
            <p className="text-xs text-gray-500">Processing time</p>
          </div>
        </div>
      </div>
    ),
    tips: "Keep your bank details updated to avoid payout delays."
  },

  // Slide 12: YOUR KPI TARGET
  {
    title: "Your KPI Target: 8% Conversion",
    subtitle: "What H E Direct expects from you",
    category: "KPI Requirement",
    icon: <Target className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-orange-50",
    content: (
      <div className="space-y-6">
        <div className="p-6 bg-teal-500/10 rounded-xl border-2 border-teal-300 text-center">
          <p className="text-sm text-teal-600 mb-2">Your Required Conversion Rate</p>
          <p className="text-6xl font-semibold text-teal-600">8%</p>
          <p className="text-lg text-gray-800 mt-2">Application to Enrollment</p>
        </div>
        <div className="p-4 bg-gray-100 rounded-xl">
          <p className="text-gray-800 font-medium mb-2">What this means:</p>
          <p className="text-gray-600 text-sm">
            For every <strong className="text-teal-600">100 applications</strong> you submit, 
            at least <strong className="text-green-600">8 students</strong> must successfully enroll.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-semibold text-blue-600">100</p>
            <p className="text-xs text-gray-500">Applications</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-semibold text-green-600">≥8</p>
            <p className="text-xs text-gray-500">Must Enroll</p>
          </div>
        </div>
      </div>
    ),
    tips: "Your KPI is measured per intake (Jan, May, Sep). It resets each intake!"
  },

  // Slide 11: KPI Dashboard
  {
    title: "Track Your KPI Performance",
    subtitle: "Your dashboard shows where you stand",
    category: "KPI Tracking",
    icon: <TrendingUp className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Your dashboard shows your KPI in real-time:</p>
        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-800">On Track (8%+ conversion)</span>
              <span className="text-green-600 font-semibold">✓ Great Work!</span>
            </div>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-800">Below Target (&lt;8%)</span>
              <span className="text-red-600 font-semibold">⚠ Needs Improvement</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          The KPI panel on your dashboard shows:
        </p>
        <FeatureList items={[
          "Your current conversion rate",
          "How many more enrollments you need",
          "Total applications this intake",
          "Stage-by-stage breakdown"
        ]} />
      </div>
    ),
    tips: "Check your KPI panel regularly and work closely with your POC to improve!"
  },

  // Slide 12: Summary
  {
    title: "You're All Set!",
    subtitle: "Start referring students today",
    category: "Summary",
    icon: <CheckCircle className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Quick action checklist to get started:</p>
        <FeatureList items={[
          "Copy your referral link and start sharing",
          "Add your first student to the platform",
          "Upload all required documents",
          "Search and apply for courses",
          "Submit to H E Direct with consent",
          "Click 'View Journey' to track application progress",
          "Track progress and hit your 8% KPI target!"
        ]} />
        <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-200">
          <p className="text-teal-600 font-medium">📞 Need Help?</p>
          <p className="text-gray-600 text-sm mt-1">Contact your POC or WhatsApp: +92 326 8096371 | Email: admin.team@hedirect.org</p>
        </div>
      </div>
    ),
    tips: "You can restart this training anytime from the Training Hub!"
  },

  // === NEW FEATURES (2026) ===
  // Slide: AI Visa Predictor for Students
  {
    title: "AI Visa Probability for Your Students",
    subtitle: "Set realistic expectations before applying",
    category: "New • AI",
    icon: <TrendingUp className="w-8 h-8 text-indigo-600" />,
    iconBg: "bg-indigo-100",
    bgColor: "bg-gradient-to-r from-indigo-50 to-violet-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Every application now ships with an AI-generated visa approval probability
          (powered by GPT) so you can advise students with data, not gut feel.
        </p>
        <FeatureList items={[
          "Open your student's application → 'Visa Probability' panel",
          "See score, risk band and counsellor notes",
          "Below 60%? The application is auto-tagged for review",
          "Use the public Probability Checker on the website for prospects"
        ]} />
      </div>
    ),
    tips: "Predictions are advisory. Use them to coach students — not to refuse applications."
  },

  // Slide: Smart Matching
  {
    title: "Smarter Course Recommendations",
    subtitle: "Academic % + English equivalence now factored in",
    category: "Enhanced",
    icon: <Target className="w-8 h-8 text-emerald-600" />,
    iconBg: "bg-emerald-100",
    bgColor: "bg-gradient-to-r from-emerald-50 to-teal-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          When you fill in a student's full academic history (Matric, Inter, Bachelors)
          and any English test score, the system ranks courses by realistic admit odds.
        </p>
        <FeatureList items={[
          "Add all percentages on Student → Profile",
          "Enter IELTS, PTE, Duolingo, or TOEFL — system converts internally",
          "Course Search results auto-sort by best match",
          "Top 3 matches highlighted with green 'Best Fit' badges"
        ]} />
      </div>
    ),
    tips: "Incomplete profiles get weaker matches. Fill everything you have."
  },

  // Slide: E-Signature Workflow
  {
    title: "Digital Agent Agreements",
    subtitle: "Sign your partnership agreement online",
    category: "Workflow",
    icon: <Shield className="w-8 h-8 text-purple-600" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-50 to-violet-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Agent agreements are now signed digitally inside the platform — no more PDF
          email chains.
        </p>
        <FeatureList items={[
          "Admin sends you an agreement link",
          "Review the terms, then type your full name to sign",
          "A signed PDF is stored against your agent profile",
          "Counter-signed copy emailed back to you"
        ]} />
      </div>
    ),
    tips: "Your signed agreement is the proof of commission rates — keep a copy."
  },

  // Slide: Persistent Document Storage
  {
    title: "Documents Never Get Lost",
    subtitle: "Files now stored in persistent cloud storage",
    category: "Reliability",
    icon: <Upload className="w-8 h-8 text-blue-600" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-50 to-cyan-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          All uploaded documents (CNICs, transcripts, offer letters) now go directly to
          encrypted persistent storage — deploys and restarts no longer wipe files.
        </p>
        <FeatureList items={[
          "Upload from Student → Documents or Application page",
          "Each file gets a secure download link",
          "Re-upload only needed if a doc was lost before this rollout",
          "Admins see the full doc trail in audit logs"
        ]} />
      </div>
    ),
    tips: "If you see 'File missing' for very old uploads, contact admin to re-upload."
  }
];

export default agentTrainingSlides;
